import { Delete, SaveAlt } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { fromBlob } from 'image-resize-compress';
import { ChangeEvent, FC, useState } from 'react';
import { useController } from 'react-hook-form';
import uniqid from 'uniqid';
import { t } from '../constants/translations';
import { useLocale } from '../contexts/locale.context';
import { storage } from '../firebase';
import PrimaryButton from './PrimaryButton';

interface Props {
  name: string;
  defaultValue?: string;
  folder: string;
  size?: number;
  asButton?: boolean;
  buttonTitle?: string;
  width?: number;
  height?: number;
}

const FormUploadImg: FC<Props> = (props) => {
  const { name, defaultValue, folder, size = 400, asButton, buttonTitle, width = 134, height = 134 } = props;
  const { lang } = useLocale();
  const [loading, setLoading] = useState(false);
  const { field } = useController({ name, defaultValue });

  const handleFormUploadImg = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLoading(true);
      const file = e.target.files[0];
      const compressedFile = await fromBlob(file, 80, size, 'auto', 'webp');
      const id = uniqid();
      const fileRef = ref(storage, `${folder}/${id}`);
      await uploadBytes(fileRef, compressedFile);
      const url = await getDownloadURL(fileRef);
      field.onChange(url);
      setLoading(false);
    }
  };

  const handleRemoveImg = async () => {
    field.onChange('');
  };

  if (asButton) {
    return (
      <Box textAlign="center">
        <PrimaryButton aria-label="upload" component="label">
          {buttonTitle}
          <input hidden accept="image/*" multiple type="file" onChange={handleFormUploadImg} />
        </PrimaryButton>
      </Box>
    );
  }

  return (
    <Box
      width={width}
      height={height}
      sx={{
        position: 'relative',
        backgroundImage: field.value ? `url(${field.value})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderStyle: 'solid',
        borderRadius: 1,
        borderWidth: 1,
        borderColor: (t) => t.palette.grey[400],
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress color="secondary" />
        </Box>
      )}

      {!loading && !field.value && (
        <Box textAlign="center" marginTop="10px">
          <input accept="image/*" id="upload" style={{ display: 'none' }} type="file" onChange={handleFormUploadImg} />
          <label htmlFor="upload">
            <IconButton color="inherit" aria-label="upload" component="span">
              <SaveAlt />
            </IconButton>
          </label>
        </Box>
      )}

      {!loading && !field.value && (
        <Typography sx={{ px: 2 }} textAlign="center" color={grey[600]}>
          {t.download_image[lang]}
        </Typography>
      )}

      {!loading && field.value && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            top: 0,
            bgcolor: (t) => `${t.palette.grey[900]}99`,
            borderRadius: 1,
            opacity: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',

            ':hover': {
              opacity: 1,
            },
          }}
        >
          <IconButton size="large" onClick={handleRemoveImg}>
            <Delete sx={{ color: (t) => t.palette.background.paper }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default FormUploadImg;
